import React, { useEffect, useState } from 'react';
import AppLayout from '../../../components/layout/AppLayout';
import { useTranslation } from 'react-i18next';
import { Input, Button, Space, Table, message, Select, Switch, Card } from 'antd';
import { EditOutlined, DeleteOutlined, PlusCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { useUserApi } from '../../../api/user/userApp';
import { TUserApp } from '../../../models/user/User';
import ConfirmationDialog from '../../../components/layout/ConfirmationDialog';
import dayjs from 'dayjs';
import { useReferential } from '../../../context/ReferentialContext';
import { CODIFICATION_ROLES, CODIFICATION_TYPE, CODIFICATION_TYPE_USERS } from '../../../constant/codification';
import { TCodification } from '../../../models/referential/referential';
import { useTilmiAuth } from '../../../hooks/useTilmiAuth';
import type { GetProps } from 'antd';
import { downloadCSV } from '../../../utils/file/csv';
import './AffectedUsersList.css';

type SearchProps = GetProps<typeof Input.Search>;

const { Search } = Input;

const AffectedUsersList: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isRoleExist } = useTilmiAuth();

    const { getAllUsersAffected, deleteUserApp, activateUserApp, deactivateUserApp } = useUserApi();
    const { getCodificationByCodes, getCodificationById, isReferentialLoaded } = useReferential();

    const [userApps, setUserApps] = useState<TUserApp[]>([]);
    const [userAppsFiltered, setUserAppsFiltered] = useState<TUserApp[]>([]);
    const [loading, setIsLoading] = useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState<boolean>(false);

    const [typeUsers, setTypeUsers] = useState<TCodification[]>([]);
    const [screenSize, setScreenSize] = useState({
        width: window.innerWidth,
        isMobile: window.innerWidth < 768,
        isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    });
    const canDeleteUser = isRoleExist(CODIFICATION_ROLES.DELETE_USER);
    const canManageActivation = isRoleExist(CODIFICATION_ROLES.ACTIVATE_DEACTIVATE);

    // Responsive check
    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            setScreenSize({
                width,
                isMobile: width < 768,
                isTablet: width >= 768 && width < 1024,
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    useEffect(() => {
        setIsLoading(true);
        getAllUsersAffected()
            .then((resp) => {
                setUserApps(resp.data);
                setUserAppsFiltered(resp.data)
            })
            .catch((err) => {
                message.error(err.response.data ?? err.message);
            })
            .finally(() => {
                setIsLoading(false);
            });

        // eslint-disable-next-line
    }, []);

    useEffect(() => {

        //Initialise typeUsers depends of connected user rights
        let usersTypes: TCodification[] = [];

        if (isRoleExist(CODIFICATION_ROLES.ADD_ADMIN)) {
            const type = getCodificationByCodes(CODIFICATION_TYPE.USER_TYPE, CODIFICATION_TYPE_USERS.ADMIN)
            if (type) {
                usersTypes.push(type)
            }
        }
        if (isRoleExist(CODIFICATION_ROLES.ADD_BUSINESS)) {
            const type = getCodificationByCodes(CODIFICATION_TYPE.USER_TYPE, CODIFICATION_TYPE_USERS.BUSINESS)
            if (type) {
                usersTypes.push(type)
            }
        }
        if (isRoleExist(CODIFICATION_ROLES.ADD_PARENT)) {
            const type = getCodificationByCodes(CODIFICATION_TYPE.USER_TYPE, CODIFICATION_TYPE_USERS.ADULT)
            if (type) {
                usersTypes.push(type)
            }
        }
        if (isRoleExist(CODIFICATION_ROLES.ADD_TEACHER)) {
            const type = getCodificationByCodes(CODIFICATION_TYPE.USER_TYPE, CODIFICATION_TYPE_USERS.TEACHER)
            if (type) {
                usersTypes.push(type)
            }
        }
        if (isRoleExist(CODIFICATION_ROLES.ADD_STUDENT)) {
            const type = getCodificationByCodes(CODIFICATION_TYPE.USER_TYPE, CODIFICATION_TYPE_USERS.MINOR)
            if (type) {
                usersTypes.push(type)
            }
        }
        if (isRoleExist(CODIFICATION_ROLES.ADD_OP_COURSE)) {
            const type = getCodificationByCodes(CODIFICATION_TYPE.USER_TYPE, CODIFICATION_TYPE_USERS.COURSE_OP)
            if (type) {
                usersTypes.push(type)
            }
        }
        setTypeUsers(usersTypes);
        // eslint-disable-next-line
    }, [isReferentialLoaded]);


    const edit = (userAppId: number) => {
        navigate('/conf/users/form', { state: { userAppId } })
    };
    const add = (typeUser: TCodification) => {
        navigate('/conf/users/form', { state: { typeUser } })
    };


    const confirmDelete = (id: number) => {
        setDeleteId(id);
        setIsDeleteDialogVisible(true);
    };

    const handleDelete = () => {
        if (deleteId == null) return;
        setDeleteLoading(true);
        deleteUserApp(deleteId)
            .then(() => {
                setUserApps(userApps.filter((userapp) => userapp.id !== deleteId));
                setUserAppsFiltered(userAppsFiltered.filter((userapp) => userapp.id !== deleteId));
                message.success(t('message.deletedSuccessfully'));
            })
            .catch((err) => {
                message.error(err.response.data ?? err.message);
            })
            .finally(() => {
                setDeleteLoading(false);
                setIsDeleteDialogVisible(false);
                setDeleteId(null);
            });
    };

    const toggleActif = async (user: TUserApp) => {
        try {
            const isActivating = user.actif === false;
            const apiCall = isActivating ? activateUserApp : deactivateUserApp;
            const { data } = await apiCall(user.id);
            const updatedUserApps = userApps.map((u) => (u.id === user.id ? data : u));
            setUserApps(updatedUserApps);
            setUserAppsFiltered(updatedUserApps);
            message.success(
                isActivating
                    ? `${user.login} ${t('admin.userActivation.messages.activated', 'has been activated')}`
                    : `${user.login} ${t('admin.userActivation.messages.deactivated', 'has been deactivated')}`
            );
        } catch (err: any) {
            message.error(err?.response?.data ?? err.message);
        }
    };

    const columnsBase: ColumnsType<TUserApp> = [
        {
            title: 'Login',
            dataIndex: 'login',
            key: 'login',
            render: (text: string, record: TUserApp) => record.login || 'N/A',
        },
        {
            title: t("text.name"),
            key: 'name',
            ellipsis: true,
            render: (_: any, record: TUserApp) => {
                if (record.personal_info) {
                    const firstname = record.personal_info.firstname || '';
                    const lastname = record.personal_info.lastname || '';
                    const birthdate = record.personal_info.birthdate
                        ? ` (${dayjs(record.personal_info.birthdate).format('YYYY-MM-DD')})`
                        : '';
                    return `${firstname} ${lastname}${birthdate}`;
                } else if (record.business_info) {
                    const name = record.business_info.name || '';
                    const taxRegistry = record.business_info.taxeregistry || '';
                    return `${name} / ${taxRegistry}`;
                }
                return 'N/A';
            },
        },
        {
            title: t("text.profil"),
            key: 'profile',
            render: (_: any, record: TUserApp) => {
                const profil = getCodificationById(record.usertype_id);
                return profil ? profil.label : 'N/A';
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right' as 'right',
            width: 150,
            render: (_: any, record: TUserApp) => (
                <Space size="middle">
                    <Button icon={<EditOutlined />} onClick={() => edit(record.id)} />
                    {canDeleteUser && (
                        <Button
                            icon={<DeleteOutlined />}
                            danger
                            onClick={() => confirmDelete(record.id)}
                            loading={deleteId === record.id && deleteLoading}
                        />
                    )}
                </Space>
            ),
        },
    ];

    const columns: ColumnsType<TUserApp> = [
        ...columnsBase.slice(0, 3),
        ...(canManageActivation ? [{
            title: t('admin.userActivation.tableHeaders.status') || 'Statut',
            dataIndex: 'actif',
            key: 'actif',
            width: 150,
            render: (value: boolean | undefined, record: TUserApp) => (
                <Switch
                    checked={record.actif !== false}
                    onChange={() => toggleActif(record)}
                    checkedChildren={t('admin.userActivation.status.active')}
                    unCheckedChildren={t('admin.userActivation.status.inactive')}
                />
            ),
        } as any] : []),
        ...columnsBase.slice(3)
    ];
    const onSearch: SearchProps['onSearch'] = (value, _e, info) => setUserAppsFiltered(userApps.filter(user => value.trim() === "" ||
        user.login.toLocaleLowerCase().includes(value.toLocaleLowerCase()) ||
        (user.business_info && user.business_info.name.toLocaleLowerCase().includes(value.toLocaleLowerCase())) ||
        (user.personal_info && user.personal_info.firstname.toLocaleLowerCase().includes(value.toLocaleLowerCase())) ||
        (user.personal_info && user.personal_info.lastname.toLocaleLowerCase().includes(value.toLocaleLowerCase()))
    ));

    function convertUserAppsToCSV(userApps: TUserApp[]): string {
        if (!userApps || !userApps.length) {
            return '';
        }

        const separator = ';';
        const headers = ['Login', 'Name', 'Profile'];

        const csvRows = userApps.map((user) => {
            const login = user.login || 'N/A';

            let name = 'N/A';
            if (user.personal_info) {
                const firstname = user.personal_info.firstname || '';
                const lastname = user.personal_info.lastname || '';
                const birthdate = user.personal_info.birthdate
                    ? ` (${dayjs(user.personal_info.birthdate).format('YYYY-MM-DD')})`
                    : '';
                name = `${firstname} ${lastname}${birthdate}`;
            } else if (user.business_info) {
                const businessName = user.business_info.name || '';
                const taxRegistry = user.business_info.taxeregistry || '';
                name = `${businessName} / ${taxRegistry}`;
            }

            const profileCodification = getCodificationById(user.usertype_id);
            const profile = profileCodification ? profileCodification.label : 'N/A';

            // Échapper les valeurs qui contiennent des séparateurs, des guillemets ou des sauts de ligne
            const escapedValues = [login, name, profile].map((value) => {
                let stringValue = value.toString().replace(/"/g, '""');
                if (stringValue.search(/("|,|\n)/g) >= 0) {
                    stringValue = `"${stringValue}"`;
                }
                return stringValue;
            });

            return escapedValues.join(separator);
        });

        // Combiner les en-têtes et les lignes de données
        const csvContent = [headers.join(separator), ...csvRows].join('\n');

        return csvContent;
    }

    const handleExportCsv = () => {
        const data = convertUserAppsToCSV(userAppsFiltered);
        downloadCSV(data, "Users.csv")
    };

    const handleBackNavigation = () => {
        navigate(-1);
    };

    return (
        <AppLayout 
            title={t('text.users')} 
            loading={loading}
            onBackNavigation={handleBackNavigation}
            showBackButton={true}
        >
            <div className="users-page-container">
                <Card className="users-filters-card" bordered={false}>
                    <div className="users-filters-row">
                        <Search
                            placeholder={t('text.search')}
                            onSearch={onSearch}
                            allowClear
                        />
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder={t('text.profil')}
                            onChange={(value: number[]) => {
                                setUserAppsFiltered(userApps.filter(user => value.length === 0 || value.includes(user.usertype_id)));
                            }}
                            maxTagCount="responsive"
                        >
                            {typeUsers.map((type: TCodification) => (
                                <Select.Option key={type.id} value={type.id}>
                                    {type.label}
                                </Select.Option>
                            ))}
                        </Select>
                        <Button icon={<DownloadOutlined />} onClick={handleExportCsv}>
                            {t('text.download')}
                        </Button>
                    </div>
                    {typeUsers.length > 0 && (
                        <div className="users-actions-section">
                            {typeUsers.map((type) => (
                                <Button
                                    key={type.id}
                                    type="primary"
                                    onClick={() => add(type)}
                                    icon={<PlusCircleOutlined />}
                                >
                                    {t('text.' + type.code)}
                                </Button>
                            ))}
                        </div>
                    )}
                </Card>

                <Card className="users-table-card" bordered={false}>
                    <Table<TUserApp>
                        columns={columns}
                        dataSource={userAppsFiltered}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            pageSize: screenSize.width < 480 ? 10 : screenSize.isMobile ? 15 : 20,
                            showSizeChanger: !screenSize.isMobile,
                            showTotal: (total, range) => 
                                `${range[0]}-${range[1]} ${t('text.of', 'of')} ${total}`,
                            showLessItems: screenSize.isMobile,
                            responsive: true,
                        }}
                        scroll={{ 
                            x: screenSize.width < 480 ? 600 : screenSize.isMobile ? 700 : 1000,
                            y: screenSize.isMobile ? 400 : undefined
                        }}
                        size={screenSize.isMobile ? 'small' : 'middle'}
                    />
                </Card>
            </div>

            <ConfirmationDialog
                visible={isDeleteDialogVisible}
                onConfirm={handleDelete}
                onCancel={() => setIsDeleteDialogVisible(false)}
                loading={deleteLoading}
                title={t('admin.user.deleteDialog.title', 'Confirm Deletion')}
                description={t('admin.user.deleteDialog.description', 'Are you sure you want to delete this user? This action is irreversible.')}
            />
        </AppLayout>
    );
};

export default AffectedUsersList;
